import type { ReasoningEffort } from "@/api/types";
import type { TFunction } from "i18next";

/**
 * Fallback codex reasoning-effort ladder.
 *
 * Both effort pickers (new-session form, mid-session popover) are
 * data-driven off the picked model's `supported_reasoning_levels`
 * from `/agent/providers/codex/models` — the ladder is per-model
 * (`ultra` is sol/terra-only), so the catalog is the real answer.
 * This list is only what we show before the catalog has answered, or
 * when the session sits on a slug the catalog no longer lists.
 *
 * It lives here rather than inline in each picker because it drifted
 * once already: the popover was still offering `minimal`-through-
 * `high` after `xhigh` shipped, so a session could not be moved to a
 * level the new-session form had been offering for releases.
 */
export const FALLBACK_REASONING_EFFORTS: readonly ReasoningEffort[] = [
  "none",
  "minimal",
  "low",
  "medium",
  "high",
  "xhigh",
  "max",
  "ultra",
] as const;

/** Claude's ``--effort`` levels, in the extended-thinking pickers. */
export const THINKING_EFFORTS = ["low", "medium", "high", "xhigh", "max"] as const;

/** An effort level as people read it; a level we have no word for yet
 *  (a model catalog can add one) is shown as its code. */
export function reasoningEffortLabel(t: TFunction, level: string): string {
  return t(`session.header.reasoning.${level}`, { defaultValue: level });
}

/** A codex reasoning-summary setting (``auto``, ``concise``…) as people read it. */
export function reasoningSummaryLabel(t: TFunction, value: string): string {
  return t(`session.reasoning_summary_option.${value}`, { defaultValue: value });
}

/** A codex verbosity setting (``low``, ``medium``, ``high``) as people read it. */
export function verbosityLabel(t: TFunction, value: string): string {
  return t(`session.verbosity_option.${value}`, { defaultValue: value });
}
